import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { motion } from 'framer-motion';
import {
  PiChatCircleText,
  PiClock,
  PiUsers,
  PiLightning,
  PiHardDrives,
  PiBrain,
  PiGlobe,
  PiChartLineUp,
  PiCheckCircle,
  PiHammer,
  PiThumbsUp,
  PiRocket,
  PiArrowRight,
  PiRoadHorizon
} from 'react-icons/pi';
import axios from 'axios';
import { useTheme } from '../../Landing/theme-provider';
import LeafletMapLight from './LeafletMapLight';
import LeafletMap from './LeafletMap';
import theme from '../phase2.styles';

// Types
interface Metric {
  value: string | number;
  label: string;
  tooltip: string;
  isLive?: boolean;
}

interface TransparencyData {
  systemUptime: Metric;
  aiResponses: Metric;
  userQueries: Metric;
  totalTrainedTokens: Metric;
  totalUsersServed: Metric;
  globalReach: Metric & { countries?: string[] };
  avgResponseTime: Metric;
  totalConversations: Metric;
  knowledgeScale: Metric;
  resolutions: Metric;
  messagesProcessed: Metric;
  busiestTime: Metric;
}

// ============================================
// LIGHT MODE STYLES (Infographic Blueprint)
// ============================================

// Design tokens for light mode (matching hero section)
const lightTokens = {
  bgMain: '#fdfdfd',           // Matches hero background
  bgPaper: '#ffffff',
  accentGold: '#D4A574',
  accentAmber: '#F59E0B',
  textInk: '#1a1a1a',          // Matches hero text
  textChalk: '#6b7280',
  textMuted: '#9ca3af',
  lineDots: '#d1d5db',         // Matches hero dot matrix
  lineBlueprint: '#e5e7eb',
  lineGold: 'rgba(212, 165, 116, 0.12)',
  lineDashed: 'rgba(0, 0, 0, 0.06)',
};

const LightSection = styled.section`
  padding: 100px 24px;  /* Matching hero padding */
  background: ${lightTokens.bgMain};
  color: ${lightTokens.textInk};
  position: relative;
  overflow: hidden;

  /* Dot Matrix Grid - matching hero */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(${lightTokens.lineDots} 1px, transparent 1px);
    background-size: 32px 32px;
    opacity: 0.5;
    pointer-events: none;
  }
`;

// Golden diagonal grid overlay
const GoldenGridOverlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: 
    linear-gradient(45deg, ${lightTokens.lineGold} 1px, transparent 1px),
    linear-gradient(-45deg, ${lightTokens.lineGold} 1px, transparent 1px);
  background-size: 60px 60px;
  opacity: 0.5;
`;

// Measurement ruler along edges
const MeasurementRuler = styled.div`
  position: absolute;
  left: 0;
  top: 100px;
  bottom: 100px;
  width: 40px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 20px 0;
  pointer-events: none;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const RulerTick = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  
  &::before {
    content: '';
    width: 16px;
    height: 1px;
    background: ${lightTokens.lineDashed};
  }
  
  span {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.55rem;
    color: ${lightTokens.textMuted};
    letter-spacing: 0.05em;
  }
`;

const LightContainer = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

// Section header with architectural styling
const SectionMarker = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  
  span.label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.7rem;
    color: ${lightTokens.textMuted};
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }
  
  span.line {
    width: 60px;
    height: 1px;
    background: linear-gradient(90deg, ${lightTokens.accentGold}, transparent);
  }
`;

const InfographicHeader = styled.div`
  text-align: center;
  margin-bottom: 64px;
  position: relative;
`;

const InfographicTitle = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 3.5rem;
  font-weight: 700;
  color: ${lightTokens.textInk};
  letter-spacing: -0.03em;
  line-height: 1.1;
  margin: 0 0 20px;
  position: relative;

  /* Underline accent */
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 120px;
    height: 3px;
    background: linear-gradient(90deg, transparent, ${lightTokens.accentGold}, transparent);
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const InfographicSubtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1.15rem;
  color: ${lightTokens.textChalk};
  line-height: 1.7;
  max-width: 600px;
  margin: 24px auto 0;
`;

// Bento Grid Layout
const BentoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-template-rows: auto;
  gap: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

// Base card styling with architectural corners
const DataCard = styled(motion.div) <{ colSpan?: number; rowSpan?: number; highlight?: boolean }>`
  grid-column: span ${props => props.colSpan || 4};
  grid-row: span ${props => props.rowSpan || 1};
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid ${lightTokens.lineBlueprint};
  padding: 28px;
  position: relative;
  transition: all 0.3s ease;

  /* Corner brackets */
  &::before, &::after {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    border-color: ${props => props.highlight ? lightTokens.accentGold : lightTokens.textMuted};
    border-style: solid;
    opacity: ${props => props.highlight ? 0.8 : 0.3};
    transition: opacity 0.3s ease;
  }

  &::before {
    top: 8px;
    left: 8px;
    border-width: 2px 0 0 2px;
  }

  &::after {
    bottom: 8px;
    right: 8px;
    border-width: 0 2px 2px 0;
  }

  &:hover {
    border-color: ${lightTokens.accentGold};
    
    &::before, &::after {
      opacity: 1;
      border-color: ${lightTokens.accentGold};
    }
  }

  @media (max-width: 1024px) {
    grid-column: span 1;
  }
`;

// Hero metric card (large)
const HeroCard = styled(DataCard)`
  background: linear-gradient(135deg, rgba(255, 252, 247, 0.95), rgba(253, 251, 247, 0.9));
  border: 2px solid ${lightTokens.lineBlueprint};
  padding: 36px;
`;

// Map container card
const MapCard = styled(DataCard)`
  padding: 0;
  overflow: hidden;
  min-height: 380px;
  
  &::before, &::after {
    z-index: 10;
  }
`;

// Pixelated icon wrapper
const PixelIcon = styled.div<{ color?: string }>`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.color ? `${props.color}10` : lightTokens.lineBlueprint};
  border: 1px dashed ${props => props.color || lightTokens.textMuted};
  position: relative;

  /* Pixel corner cuts */
  clip-path: polygon(
    0 4px, 4px 4px, 4px 0,
    calc(100% - 4px) 0, calc(100% - 4px) 4px, 100% 4px,
    100% calc(100% - 4px), calc(100% - 4px) calc(100% - 4px), calc(100% - 4px) 100%,
    4px 100%, 4px calc(100% - 4px), 0 calc(100% - 4px)
  );

  svg {
    color: ${props => props.color || lightTokens.textInk};
  }
`;

// Metric value with annotation
const MetricDisplay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const MetricValue = styled.div<{ size?: 'large' | 'medium' | 'small' }>`
  font-family: 'Space Grotesk', sans-serif;
  font-size: ${props =>
    props.size === 'large' ? '3.5rem' :
      props.size === 'medium' ? '2rem' : '1.5rem'};
  font-weight: 700;
  color: ${lightTokens.textInk};
  letter-spacing: -0.03em;
  line-height: 1;
`;

const MetricLabel = styled.div<{ highlight?: boolean }>`
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.72rem;
  color: ${props => props.highlight ? lightTokens.accentGold : lightTokens.textChalk};
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const MetricAnnotation = styled.div`
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.7rem;
  color: ${lightTokens.textMuted};
  font-style: italic;
  margin-top: 8px;
  
  &::before {
    content: '↳ ';
    color: ${lightTokens.accentGold};
  }
`;

// Progress gauge
const ProgressGauge = styled.div`
  width: 100%;
  height: 8px;
  background: ${lightTokens.lineBlueprint};
  margin-top: 16px;
  position: relative;
  overflow: hidden;

  /* Tick marks */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: repeating-linear-gradient(
      90deg,
      transparent,
      transparent 9%,
      ${lightTokens.bgMain} 9%,
      ${lightTokens.bgMain} 10%
    );
    z-index: 2;
  }
`;

const GaugeFill = styled(motion.div) <{ color?: string }>`
  height: 100%;
  background: linear-gradient(90deg, ${props => props.color || lightTokens.accentGold}, ${props => props.color || lightTokens.accentAmber});
`;

// Chalk annotation floating note
const ChalkNote = styled.div<{ position?: 'top-right' | 'bottom-left' }>`
  position: absolute;
  ${props => props.position === 'top-right' ? 'top: 12px; right: 12px;' : 'bottom: 12px; left: 12px;'}
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.6rem;
  color: ${lightTokens.textMuted};
  letter-spacing: 0.05em;
  padding: 4px 8px;
  background: ${lightTokens.bgPaper};
  border: 1px dashed ${lightTokens.lineDashed};
`;

// Map overlay with blueprint styling
const MapOverlay = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 10;
  background: rgba(255, 252, 247, 0.95);
  padding: 20px 24px;
  border: 1px solid ${lightTokens.lineBlueprint};
  pointer-events: none;

  /* Corner accent */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: ${lightTokens.accentGold};
  }
`;

const MapScale = styled.div`
  position: absolute;
  bottom: 16px;
  right: 16px;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.6rem;
  color: ${lightTokens.textMuted};
  
  &::before {
    content: '';
    width: 40px;
    height: 2px;
    background: ${lightTokens.textMuted};
  }
`;

// Small metric card content
const SmallCardContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  height: 100%;
`;

const SmallCardText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

// Loading state
const LoadingState = styled.div<{ isDark: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  font-family: ${props => props.isDark ? theme.fonts.mono : "'JetBrains Mono', monospace"};
  color: ${props => props.isDark ? theme.colors.textDark : lightTokens.textMuted};
`;

// ============================================
// DARK MODE STYLES (Bento Grid)
// ============================================

const DarkSection = styled.section`
    padding: 80px 24px;
    position: relative;

    @media (max-width: 768px) {
        padding: 60px 16px;
    }
`;

const DarkContentWrapper = styled.div`
    max-width: 1400px;
    margin: 0 auto;
`;

const DarkSectionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 48px;
    border-bottom: 1px solid ${theme.colors.border};
    padding-bottom: 24px;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }
`;

const DarkTitleGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const DarkLiveIndicator = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    background: rgba(16, 185, 129, 0.1);
    border: 1px solid rgba(16, 185, 129, 0.2);
    border-radius: 99px;
    color: ${theme.colors.successLight};
    font-family: ${theme.fonts.mono};
    font-size: 0.7rem;
    font-weight: 500;
    letter-spacing: 0.08em;
    width: fit-content;
`;

const DarkPulseDot = styled.div`
    width: 6px;
    height: 6px;
    background-color: ${theme.colors.successLight};
    border-radius: 50%;
    position: relative;

    &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 100%;
        height: 100%;
        background-color: inherit;
        border-radius: 50%;
        animation: ping 2s cubic-bezier(0, 0, 0.2, 1) infinite;
    }

    @keyframes ping {
        75%, 100% {
            transform: translate(-50%, -50%) scale(3);
            opacity: 0;
        }
    }
`;

const DarkSectionTitle = styled.h2`
    font-family: ${theme.fonts.sans};
    font-size: 2.5rem;
    font-weight: 600;
    color: ${theme.colors.textWhite};
    margin: 0;
    letter-spacing: -0.03em;

    @media (max-width: 768px) {
        font-size: 2rem;
    }
`;

const DarkSectionSubtitle = styled.p`
    font-family: ${theme.fonts.sans};
    font-size: 1rem;
    color: ${theme.colors.textMuted};
    margin: 0;
    max-width: 500px;
`;

const DarkLastUpdated = styled.div`
    font-family: ${theme.fonts.mono};
    font-size: 0.75rem;
    color: ${theme.colors.textDark};
    text-align: right;
`;

const DarkBentoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-auto-rows: minmax(180px, auto);
    gap: 16px;

    @media (max-width: 1024px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

const DarkGlassCard = styled(motion.div) <{ colSpan?: number; rowSpan?: number; highlight?: boolean }>`
    grid-column: span ${props => props.colSpan || 1};
    grid-row: span ${props => props.rowSpan || 1};
    background: ${props => props.highlight
    ? 'radial-gradient(120% 120% at 50% 10%, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 100%)'
    : theme.glass.background};
    backdrop-filter: ${theme.glass.blur};
    border: ${theme.glass.border};
    border-radius: ${theme.glass.borderRadius};
    padding: 24px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    transition: border-color 0.3s ease;
    position: relative;
    overflow: hidden;

    &:hover {
        border-color: ${theme.colors.borderHover};
    }

    ${props => props.highlight && css`
        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 1px;
            background: linear-gradient(90deg, transparent, ${theme.colors.accent}, transparent);
            opacity: 0.5;
        }
    `}

    @media (max-width: 1024px) {
        grid-column: span ${props => Math.min(props.colSpan || 1, 2)};
    }

    @media (max-width: 640px) {
        grid-column: span 1;
    }
`;

const DarkMapContainer = styled(DarkGlassCard)`
    padding: 0;
    position: relative;
    min-height: 400px;
`;

const DarkMapHUD = styled.div`
    position: absolute;
    top: 24px;
    left: 24px;
    z-index: 10;
    background: rgba(0, 0, 0, 0.7);
    border: 1px solid ${theme.colors.border};
    backdrop-filter: blur(8px);
    padding: 12px 16px;
    border-radius: 8px;
    pointer-events: none;
`;

const DarkMetricTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: ${theme.fonts.sans};
    font-size: 0.85rem;
    font-weight: 500;
    color: ${theme.colors.textMuted};
    margin-bottom: 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

const DarkMetricValue = styled.div`
    font-family: ${theme.fonts.mono};
    font-size: 2rem;
    font-weight: 700;
    color: ${theme.colors.textWhite};
    letter-spacing: -0.05em;
    line-height: 1;

    @media (max-width: 480px) {
        font-size: 1.75rem;
    }
`;

const DarkMetricSub = styled.div`
    font-family: ${theme.fonts.mono};
    font-size: 0.75rem;
    color: ${theme.colors.textDark};
    margin-top: 8px;
`;

const DarkProgressBar = styled.div`
    width: 100%;
    height: 4px;
    background: ${theme.colors.surfaceSolid};
    margin-top: 16px;
    border-radius: 2px;
    overflow: hidden;
`;

const DarkProgressFill = styled(motion.div)`
    height: 100%;
    background: linear-gradient(90deg, ${theme.colors.accent}, ${theme.colors.accentLight});
`;

// ============================================
// ROADMAP DATA & STYLES
// ============================================

const roadmapData = {
  shipped: [
    'Dynamic Conversational Forms',
    'BYOK Architecture',
    'URL + PDF Training',
    'Live Metrics Dashboard',
  ],
  inProgress: [
    'Smart Human Handoff',
    'Video Transcription',
    'Agency Partner Dashboard',
  ],
  upNext: [
    { text: 'HubSpot Integration', votes: 128 },
    { text: 'SMS Notifications', votes: 94 },
    { text: 'Slack Integration', votes: 76 },
  ],
};

// Light mode roadmap styles
const RoadmapCard = styled(DataCard)`
    padding: 32px;
`;

const RoadmapHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px dashed ${lightTokens.lineDashed};
`;

const RoadmapTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.25rem;
    font-weight: 600;
    color: ${lightTokens.textInk};
`;

const RoadmapLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.75rem;
    color: ${lightTokens.accentGold};
    text-decoration: none;
    letter-spacing: 0.05em;
    transition: gap 0.2s ease;

    &:hover {
        gap: 10px;
    }
`;

const RoadmapGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 20px;
    }
`;

const RoadmapColumn = styled.div``;

const RoadmapColumnHeader = styled.div<{ status: 'shipped' | 'progress' | 'voting' }>`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid ${lightTokens.lineBlueprint};
`;

const RoadmapStatusIcon = styled.div<{ status: 'shipped' | 'progress' | 'voting' }>`
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${props => {
    switch (props.status) {
      case 'shipped': return 'rgba(16, 185, 129, 0.1)';
      case 'progress': return 'rgba(139, 92, 246, 0.1)';
      case 'voting': return 'rgba(59, 130, 246, 0.1)';
    }
  }};
    border: 1px dashed ${props => {
    switch (props.status) {
      case 'shipped': return 'rgba(16, 185, 129, 0.3)';
      case 'progress': return 'rgba(139, 92, 246, 0.3)';
      case 'voting': return 'rgba(59, 130, 246, 0.3)';
    }
  }};
`;

const RoadmapColumnTitle = styled.div`
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    color: ${lightTokens.textMuted};
    text-transform: uppercase;
`;

const RoadmapItemsList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`;

const RoadmapItem = styled.li<{ status: 'shipped' | 'progress' | 'voting' }>`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 0;
    border-bottom: 1px solid ${lightTokens.lineDashed};
    font-family: 'Inter', sans-serif;
    font-size: 0.85rem;
    color: ${lightTokens.textChalk};
    line-height: 1.4;

    &:last-child {
        border-bottom: none;
    }

    svg {
        flex-shrink: 0;
    }
`;

const VoteCount = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-left: auto;
    padding: 2px 8px;
    background: rgba(59, 130, 246, 0.08);
    border: 1px dashed rgba(59, 130, 246, 0.2);
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.65rem;
    color: #3b82f6;
    font-weight: 500;
`;

// Dark mode roadmap styles
const DarkRoadmapGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-top: 16px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const DarkRoadmapColumn = styled.div`
    background: rgba(255, 255, 255, 0.02);
    border: 1px solid ${theme.colors.border};
    border-radius: 8px;
    padding: 16px;
`;

const DarkRoadmapColumnHeader = styled.div<{ status: 'shipped' | 'progress' | 'voting' }>`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid ${theme.colors.border};
`;

const DarkRoadmapItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 0;
    font-family: ${theme.fonts.sans};
    font-size: 0.85rem;
    color: ${theme.colors.textMuted};

    svg {
        flex-shrink: 0;
    }
`;

const DarkVoteCount = styled.span`
    margin-left: auto;
    padding: 2px 8px;
    background: rgba(59, 130, 246, 0.15);
    border-radius: 4px;
    font-family: ${theme.fonts.mono};
    font-size: 0.65rem;
    color: #60a5fa;
`;

// Component
const LiveMetricsDashboard: React.FC = () => {
  const { theme: currentTheme } = useTheme() as { theme: string };
  const isDark = currentTheme === 'dark';
  const [data, setData] = useState<TransparencyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get('/api/transparency/metrics');
        setData(response.data);
        setLastUpdated(new Date());
      } catch (error) {
        console.error('Failed to fetch transparency metrics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const totalConversations = data ? (typeof data.totalConversations.value === 'number'
    ? data.totalConversations.value
    : parseInt(String(data.totalConversations.value).replace(/,/g, '')) || 0) : 0;
  const hoursSaved = Math.round((totalConversations * 5) / 60);

  if (loading) {
    if (isDark) {
      return (
        <DarkSection id="live-metrics">
          <DarkContentWrapper>
            <LoadingState isDark={true}>Initializing Live Telemetry...</LoadingState>
          </DarkContentWrapper>
        </DarkSection>
      );
    }
    return (
      <LightSection id="live-metrics">
        <LightContainer>
          <LoadingState isDark={false}>Loading metrics...</LoadingState>
        </LightContainer>
      </LightSection>
    );
  }

  if (!data) return null;

  // ============================================
  // DARK MODE RENDER (Bento Grid)
  // ============================================
  if (isDark) {
    return (
      <DarkSection id="live-metrics">
        <DarkContentWrapper>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <DarkSectionHeader>
              <DarkTitleGroup>
                <DarkLiveIndicator>
                  <DarkPulseDot />
                  LIVE METRICS
                </DarkLiveIndicator>
                <DarkSectionTitle>Open Metrics Dashboard</DarkSectionTitle>
                <DarkSectionSubtitle>
                  We believe in radical transparency. Real-time performance data from our production environment.
                </DarkSectionSubtitle>
              </DarkTitleGroup>
              <DarkLastUpdated>
                LAST UPDATED: {lastUpdated.toLocaleTimeString()}
              </DarkLastUpdated>
            </DarkSectionHeader>

            <DarkBentoGrid>
              {/* Hero Metric: Hours Saved */}
              <DarkGlassCard colSpan={2} highlight>
                <DarkMetricTitle>
                  <PiChartLineUp size={18} color={theme.colors.accent} />
                  HUMAN HOURS SAVED
                </DarkMetricTitle>
                <div>
                  <DarkMetricValue style={{ fontSize: '3.5rem' }}>
                    {hoursSaved.toLocaleString()}
                  </DarkMetricValue>
                  <DarkMetricSub>Equivalent to {Math.round(hoursSaved / 40)} work weeks</DarkMetricSub>
                </div>
              </DarkGlassCard>

              {/* Uptime */}
              <DarkGlassCard>
                <DarkMetricTitle>
                  <PiHardDrives size={18} color={theme.colors.success} />
                  SYSTEM UPTIME
                </DarkMetricTitle>
                <DarkMetricValue>{data.systemUptime.value}</DarkMetricValue>
                <DarkMetricSub>Since Jan 1, 2024</DarkMetricSub>
              </DarkGlassCard>

              {/* Latency */}
              <DarkGlassCard>
                <DarkMetricTitle>
                  <PiClock size={18} color={theme.colors.info} />
                  AVG LATENCY
                </DarkMetricTitle>
                <DarkMetricValue>{data.avgResponseTime.value}</DarkMetricValue>
                <DarkMetricSub>TTFT (Time to First Token)</DarkMetricSub>
              </DarkGlassCard>

              {/* Map */}
              <DarkMapContainer colSpan={2} rowSpan={2}>
                <DarkMapHUD>
                  <div style={{ fontSize: '0.75rem', color: theme.colors.textMuted, marginBottom: '4px' }}>
                    LIVE DEPLOYMENTS
                  </div>
                  <div style={{ fontFamily: theme.fonts.mono, fontSize: '1.25rem', color: theme.colors.textWhite }}>
                    {String(data.globalReach.value).split(' ')[0]} Regions
                  </div>
                </DarkMapHUD>
                <div style={{ width: '100%', height: '100%' }}>
                  <LeafletMap activeCountries={data.globalReach.countries || []} />
                </div>
              </DarkMapContainer>

              {/* Resolution Rate */}
              <DarkGlassCard>
                <DarkMetricTitle>
                  <PiChartLineUp size={18} color={theme.colors.purple} />
                  AUTO-RESOLVED
                </DarkMetricTitle>
                <DarkMetricValue>{data.resolutions.value}</DarkMetricValue>
                <DarkMetricSub>Without Human Handoff</DarkMetricSub>
              </DarkGlassCard>

              {/* Total Users */}
              <DarkGlassCard>
                <DarkMetricTitle>
                  <PiUsers size={18} />
                  USERS INTERACTED
                </DarkMetricTitle>
                <DarkMetricValue>{data.totalUsersServed.value}</DarkMetricValue>
                <DarkMetricSub>Unique Conversations</DarkMetricSub>
              </DarkGlassCard>

              {/* Messages Processed */}
              <DarkGlassCard colSpan={2}>
                <DarkMetricTitle>
                  <PiChatCircleText size={18} />
                  TOTAL MESSAGES PROCESSED
                </DarkMetricTitle>
                <DarkMetricValue>{data.messagesProcessed.value}</DarkMetricValue>
                <DarkProgressBar>
                  <DarkProgressFill
                    initial={{ width: 0 }}
                    whileInView={{ width: '100%' }}
                    transition={{ duration: 1.5 }}
                    viewport={{ once: true }}
                  />
                </DarkProgressBar>
              </DarkGlassCard>

              {/* Trained Tokens */}
              <DarkGlassCard colSpan={2}>
                <DarkMetricTitle>
                  <PiBrain size={18} color={theme.colors.yellow} />
                  KNOWLEDGE BASE SIZE
                </DarkMetricTitle>
                <DarkMetricValue>{data.totalTrainedTokens.value}</DarkMetricValue>
                <DarkMetricSub>Trained Tokens Across All Deployments</DarkMetricSub>
              </DarkGlassCard>

              {/* Roadmap Section */}
              <DarkGlassCard colSpan={4}>
                <DarkMetricTitle>
                  <PiRoadHorizon size={18} color="#8b5cf6" />
                  PRODUCT ROADMAP
                </DarkMetricTitle>
                <DarkRoadmapGrid>
                  {/* Shipped */}
                  <DarkRoadmapColumn>
                    <DarkRoadmapColumnHeader status="shipped">
                      <PiCheckCircle size={14} color="#10b981" />
                      <span style={{ fontFamily: theme.fonts.mono, fontSize: '0.7rem', color: theme.colors.textMuted, letterSpacing: '0.08em' }}>SHIPPED</span>
                    </DarkRoadmapColumnHeader>
                    {roadmapData.shipped.map((item, index) => (
                      <DarkRoadmapItem key={index}>
                        <PiCheckCircle size={14} color="#10b981" />
                        {item}
                      </DarkRoadmapItem>
                    ))}
                  </DarkRoadmapColumn>

                  {/* In Progress */}
                  <DarkRoadmapColumn>
                    <DarkRoadmapColumnHeader status="progress">
                      <PiHammer size={14} color="#8b5cf6" />
                      <span style={{ fontFamily: theme.fonts.mono, fontSize: '0.7rem', color: theme.colors.textMuted, letterSpacing: '0.08em' }}>IN PROGRESS</span>
                    </DarkRoadmapColumnHeader>
                    {roadmapData.inProgress.map((item, index) => (
                      <DarkRoadmapItem key={index}>
                        <PiHammer size={14} color="#8b5cf6" />
                        {item}
                      </DarkRoadmapItem>
                    ))}
                  </DarkRoadmapColumn>

                  {/* Up Next */}
                  <DarkRoadmapColumn>
                    <DarkRoadmapColumnHeader status="voting">
                      <PiThumbsUp size={14} color="#3b82f6" />
                      <span style={{ fontFamily: theme.fonts.mono, fontSize: '0.7rem', color: theme.colors.textMuted, letterSpacing: '0.08em' }}>UP NEXT</span>
                    </DarkRoadmapColumnHeader>
                    {roadmapData.upNext.map((item, index) => (
                      <DarkRoadmapItem key={index}>
                        <PiThumbsUp size={14} color="#3b82f6" />
                        {item.text}
                        <DarkVoteCount>{item.votes}</DarkVoteCount>
                      </DarkRoadmapItem>
                    ))}
                  </DarkRoadmapColumn>
                </DarkRoadmapGrid>
              </DarkGlassCard>
            </DarkBentoGrid>
          </motion.div>
        </DarkContentWrapper>
      </DarkSection>
    );
  }

  // ============================================
  // LIGHT MODE RENDER (Infographic Blueprint)
  // ============================================
  return (
    <LightSection id="live-metrics">
      {/* Background layers */}
      <GoldenGridOverlay />
      <MeasurementRuler>
        <RulerTick><span>01</span></RulerTick>
        <RulerTick><span>02</span></RulerTick>
        <RulerTick><span>03</span></RulerTick>
        <RulerTick><span>04</span></RulerTick>
        <RulerTick><span>05</span></RulerTick>
      </MeasurementRuler>

      <LightContainer>
        {/* Header Section */}
        <InfographicHeader>
          <SectionMarker>
            <span className="label">[Section A.01]</span>
            <span className="line" />
          </SectionMarker>
          <InfographicTitle>
            Attracting Love,<br />Like Nobody's Business
          </InfographicTitle>
          <InfographicSubtitle>
            We're quickly catching up with users across the globe. Our vision is relentless; to set the
            <strong> global standard for AI chatbots</strong>.
          </InfographicSubtitle>
        </InfographicHeader>

        {/* Bento Grid Layout */}
        <BentoGrid>
          {/* Hero Metric: Hours Saved */}
          <HeroCard
            colSpan={5}
            highlight
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <ChalkNote position="top-right">IMPACT METRIC</ChalkNote>
            <SmallCardContent style={{ alignItems: 'center' }}>
              <PixelIcon color={lightTokens.accentGold}>
                <PiLightning size={22} />
              </PixelIcon>
              <MetricDisplay style={{ flex: 1 }}>
                <MetricLabel highlight>Human Hours Saved</MetricLabel>
                <MetricValue size="large">{hoursSaved.toLocaleString()}</MetricValue>
                <MetricAnnotation>Equivalent to {Math.round(hoursSaved / 40)} work weeks</MetricAnnotation>
              </MetricDisplay>
            </SmallCardContent>
            <ProgressGauge>
              <GaugeFill
                initial={{ width: 0 }}
                whileInView={{ width: '78%' }}
                transition={{ duration: 1.2, delay: 0.3 }}
                viewport={{ once: true }}
              />
            </ProgressGauge>
          </HeroCard>

          {/* Map Section */}
          <MapCard
            colSpan={7}
            rowSpan={2}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <MapOverlay>
              <MetricValue size="medium">{String(data.globalReach.value).split(' ')[0]}</MetricValue>
              <MetricLabel style={{ marginTop: '4px' }}>
                <PiGlobe size={12} style={{ marginRight: '4px' }} />
                Countries Active
              </MetricLabel>
            </MapOverlay>
            <MapScale>SCALE: 1:50M</MapScale>
            <div style={{ width: '100%', height: '100%', position: 'absolute', inset: 0 }}>
              <LeafletMapLight activeCountries={data.globalReach.countries || []} />
            </div>
          </MapCard>

          {/* System Uptime */}
          <DataCard
            colSpan={2}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <SmallCardContent>
              <PixelIcon color="#10B981">
                <PiHardDrives size={18} />
              </PixelIcon>
              <SmallCardText>
                <MetricLabel>System Uptime</MetricLabel>
                <MetricValue size="small">{data.systemUptime.value}</MetricValue>
              </SmallCardText>
            </SmallCardContent>
          </DataCard>

          {/* Avg Response Time */}
          <DataCard
            colSpan={3}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.25 }}
          >
            <SmallCardContent>
              <PixelIcon color="#6366F1">
                <PiClock size={18} />
              </PixelIcon>
              <SmallCardText>
                <MetricLabel>Avg Response</MetricLabel>
                <MetricValue size="small">{data.avgResponseTime.value}</MetricValue>
                <MetricAnnotation>Time to First Token</MetricAnnotation>
              </SmallCardText>
            </SmallCardContent>
          </DataCard>

          {/* AI Responses */}
          <DataCard
            colSpan={4}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            <SmallCardContent>
              <PixelIcon color="#06B6D4">
                <PiChatCircleText size={18} />
              </PixelIcon>
              <SmallCardText>
                <MetricLabel>AI Responses</MetricLabel>
                <MetricValue size="small">{data.aiResponses?.value || data.messagesProcessed.value}</MetricValue>
              </SmallCardText>
            </SmallCardContent>
            <ProgressGauge>
              <GaugeFill
                color="#06B6D4"
                initial={{ width: 0 }}
                whileInView={{ width: '100%' }}
                transition={{ duration: 1, delay: 0.5 }}
                viewport={{ once: true }}
              />
            </ProgressGauge>
          </DataCard>

          {/* Users Served */}
          <DataCard
            colSpan={4}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.35 }}
          >
            <SmallCardContent>
              <PixelIcon color="#8B5CF6">
                <PiUsers size={18} />
              </PixelIcon>
              <SmallCardText>
                <MetricLabel>Users Served</MetricLabel>
                <MetricValue size="small">{data.totalUsersServed.value}</MetricValue>
                <MetricAnnotation>Unique conversations</MetricAnnotation>
              </SmallCardText>
            </SmallCardContent>
          </DataCard>

          {/* Knowledge Base */}
          <DataCard
            colSpan={4}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.4 }}
          >
            <SmallCardContent>
              <PixelIcon color="#F59E0B">
                <PiBrain size={18} />
              </PixelIcon>
              <SmallCardText>
                <MetricLabel>Trained Tokens</MetricLabel>
                <MetricValue size="small">{data.totalTrainedTokens.value}</MetricValue>
              </SmallCardText>
            </SmallCardContent>
            <ProgressGauge>
              <GaugeFill
                color="#F59E0B"
                initial={{ width: 0 }}
                whileInView={{ width: '65%' }}
                transition={{ duration: 1, delay: 0.6 }}
                viewport={{ once: true }}
              />
            </ProgressGauge>
          </DataCard>

          {/* Roadmap Section */}
          <RoadmapCard
            colSpan={12}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <ChalkNote position="top-right">PUBLIC ROADMAP</ChalkNote>
            <RoadmapHeader>
              <RoadmapTitle>
                <PixelIcon color="#8b5cf6">
                  <PiRocket size={18} />
                </PixelIcon>
                Product Roadmap
              </RoadmapTitle>
              <RoadmapLink href="/roadmap">
                View Full Roadmap
                <PiArrowRight size={14} />
              </RoadmapLink>
            </RoadmapHeader>

            <RoadmapGrid>
              {/* Shipped */}
              <RoadmapColumn>
                <RoadmapColumnHeader status="shipped">
                  <RoadmapStatusIcon status="shipped">
                    <PiCheckCircle size={12} color="#10b981" />
                  </RoadmapStatusIcon>
                  <RoadmapColumnTitle>Shipped</RoadmapColumnTitle>
                </RoadmapColumnHeader>
                <RoadmapItemsList>
                  {roadmapData.shipped.map((item, index) => (
                    <RoadmapItem key={index} status="shipped">
                      <PiCheckCircle size={14} color="#10b981" />
                      {item}
                    </RoadmapItem>
                  ))}
                </RoadmapItemsList>
              </RoadmapColumn>

              {/* In Progress */}
              <RoadmapColumn>
                <RoadmapColumnHeader status="progress">
                  <RoadmapStatusIcon status="progress">
                    <PiHammer size={12} color="#8b5cf6" />
                  </RoadmapStatusIcon>
                  <RoadmapColumnTitle>In Progress</RoadmapColumnTitle>
                </RoadmapColumnHeader>
                <RoadmapItemsList>
                  {roadmapData.inProgress.map((item, index) => (
                    <RoadmapItem key={index} status="progress">
                      <PiHammer size={14} color="#8b5cf6" />
                      {item}
                    </RoadmapItem>
                  ))}
                </RoadmapItemsList>
              </RoadmapColumn>

              {/* Up Next */}
              <RoadmapColumn>
                <RoadmapColumnHeader status="voting">
                  <RoadmapStatusIcon status="voting">
                    <PiThumbsUp size={12} color="#3b82f6" />
                  </RoadmapStatusIcon>
                  <RoadmapColumnTitle>Up Next (Votes)</RoadmapColumnTitle>
                </RoadmapColumnHeader>
                <RoadmapItemsList>
                  {roadmapData.upNext.map((item, index) => (
                    <RoadmapItem key={index} status="voting">
                      <PiThumbsUp size={14} color="#3b82f6" />
                      {item.text}
                      <VoteCount>{item.votes}</VoteCount>
                    </RoadmapItem>
                  ))}
                </RoadmapItemsList>
              </RoadmapColumn>
            </RoadmapGrid>
          </RoadmapCard>
        </BentoGrid>
      </LightContainer>
    </LightSection>
  );
};

export default LiveMetricsDashboard;
